import { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useMatch } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

import { createApiClient } from './api.js';
import { ErrorBoundary } from './ErrorBoundary.jsx';
import { CommandPalette } from './components/CommandPalette.jsx';
import { HeaderBar } from './components/HeaderBar.jsx';
import { Sidebar } from './components/Sidebar.jsx';
import { UpdateAvailableBanner } from './components/UpdateAvailableBanner.jsx';
import { useResolvedTenant } from './hooks/useResolvedTenant.js';

const MOBILE_NAV_EVENT = 'spotlight:mobile-nav';

/**
 * Layout chrome for every authenticated page (card 050 — v2 IA).
 *
 *   ┌──────────────────────────────────────────────────────────┐
 *   │ Header bar — tenant + project breadcrumbs + user menu    │
 *   ├──────────┬───────────────────────────────────────────────┤
 *   │ Sidebar  │ <Outlet />                                    │
 *   │ Inbox /  │                                               │
 *   │ Authoring│                                               │
 *   │ Insights │                                               │
 *   │ Setup ▾  │                                               │
 *   │ Tenant ▾ │                                               │
 *   └──────────┴───────────────────────────────────────────────┘
 *
 * What moved out of Shell vs the v1 layout:
 *
 *   - Tenant switcher dropdown   → HeaderBar / TenantPicker.
 *   - Project context tag         → HeaderBar / ProjectPicker.
 *   - Clerk UserButton + name     → HeaderBar / UserMenu (consolidated
 *                                   with the old "You" sidebar section).
 *
 * What Shell still owns:
 *
 *   - The grid layout itself (header / aside / main).
 *   - Polling the help-inbox unread + pending-access-request counts
 *     that the Sidebar surfaces as badges.  Keeping the polling in
 *     one place avoids each section spinning up its own interval.
 *   - The route-keyed ErrorBoundary that resets on navigation.
 */
export function Shell() {
  const { getToken } = useAuth();
  const location = useLocation();
  const projectMatch = useMatch('/projects/:slug/*');
  const projectSlug = projectMatch?.params?.slug;
  // Card 097 — gate every /v1/admin/* fetch on a *resolved* tenant
  // (one that matches the user's actual memberships).  The previous
  // currentTenantId() fallback to DEMO_TENANT_UUID was always truthy,
  // so the old `if (!activeTenant)` gate never fired and the sidebar
  // badge poll 404'd on every fresh sign-in.
  const { isResolved } = useResolvedTenant();

  // Sidebar badge counters — help-inbox unread + pending access
  // requests across every project the caller can see.  Server already
  // scopes these by admin project ids; we just poll every 60 s so the
  // sidebar stays fresh without a manual refresh.
  const api = useMemo(() => createApiClient(getToken), [getToken]);
  const [helpUnread, setHelpUnread] = useState(0);
  const [pendingAccess, setPendingAccess] = useState(0);
  useEffect(() => {
    if (!isResolved) return undefined;
    let alive = true;
    const tick = async () => {
      try {
        const [inbox, access] = await Promise.all([
          api('/v1/admin/help/requests').catch(() => null),
          api('/v1/admin/access-requests?status_filter=pending').catch(() => null),
        ]);
        if (!alive) return;
        const items = inbox?.items || [];
        setHelpUnread(items.filter((r) => r.operator_unread).length);
        const reqs = Array.isArray(access?.items)
          ? access.items
          : (Array.isArray(access) ? access : []);
        setPendingAccess(reqs.length);
      } catch {
        /* badges are non-essential — silent failure keeps the chrome calm */
      }
    };
    tick();
    const t = setInterval(tick, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, [api, isResolved]);

  // Mobile slide-over navigation (card 113 — mobile chrome).  On
  // narrow viewports the sidebar collapses off-screen and a
  // hamburger button in the HeaderBar reveals it via slide-from-
  // left + dim backdrop.  Coordination happens via a window event
  // so the toggle, the backdrop, and the close-on-link-click paths
  // all share one source of truth without prop-drilling.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);

  useEffect(() => {
    const onToggle = (e) => {
      if (e?.detail?.state === 'open') setMobileNavOpen(true);
      else if (e?.detail?.state === 'close') setMobileNavOpen(false);
      else setMobileNavOpen((v) => !v);
    };
    window.addEventListener(MOBILE_NAV_EVENT, onToggle);
    return () => window.removeEventListener(MOBILE_NAV_EVENT, onToggle);
  }, []);

  // Close on route change so the operator never lands on a new page
  // with the panel still over the content.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  // Body-scroll lock + Escape-to-close while the slide-over is up.
  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') setMobileNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [mobileNavOpen]);

  return (
    <div className={`shell${mobileNavOpen ? ' shell--mobile-nav-open' : ''}`}>
      {/* Card 110 — system-notice slot ABOVE the HeaderBar.  Renders
          null until the SW signals an update is waiting, at which
          point the grid's `auto` row reflows to fit its height
          exactly (the HeaderBar / sidebar / main don't move beyond
          that height — they stay anchored via their own sticky
          positioning once the user scrolls past). */}
      <div className="shell__banner">
        <UpdateAvailableBanner />
      </div>
      <HeaderBar />
      <aside
        className="shell__aside"
        aria-hidden={mobileNavOpen ? undefined : (typeof window !== 'undefined' && window.innerWidth <= 720 ? 'true' : undefined)}
      >
        <Sidebar
          projectSlug={projectSlug}
          badges={{ helpUnread, pendingAccess }}
        />
      </aside>
      {/* Mobile backdrop — only hits the DOM when the panel is open
          so on desktop the layout has zero extra nodes. */}
      {mobileNavOpen && (
        <button
          type="button"
          className="shell__backdrop"
          aria-label="Close navigation"
          onClick={closeMobileNav}
        />
      )}
      <main className="shell__main">
        <ErrorBoundary key={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </main>
      {/* Card spotlight-067 — global Cmd-K command palette.  Single
          mount lives here so the hotkey listener is attached once
          and the overlay survives route changes. */}
      <CommandPalette />
      <style>{SHELL_CSS}</style>
    </div>
  );
}

export { MOBILE_NAV_EVENT };

const SHELL_CSS = `
  .shell {
    display: grid;
    grid-template-columns: 248px 1fr;
    /* Card 110 — first row is the system-notice banner slot.  Sized
       auto so it collapses to 0 height when <UpdateAvailableBanner>
       renders null (the common case), and grows to fit when the SW
       signals an update is waiting.  The HeaderBar / aside / main
       grid areas don't reflow further than that height — they stay
       sticky once the user scrolls. */
    grid-template-rows: auto 56px 1fr;
    grid-template-areas:
      "banner banner"
      "header header"
      "aside  main";
    min-height: 100vh;
    background: var(--surface-1, #f8fafc);
  }

  .shell__banner {
    grid-area: banner;
    /* No background of its own — the banner component paints its own
       brand-tinted surface.  This wrapper exists so the grid row
       collapses cleanly when the component returns null. */
  }

  .shell__aside {
    grid-area: aside;
    background: var(--surface-0, #ffffff);
    border-right: 1px solid var(--border-1, #e2e8f0);
    display: flex;
    flex-direction: column;
    /* Top is the bottom of the sticky header bar (56px) */
    position: sticky;
    top: 56px;
    height: calc(100vh - 56px);
    min-height: 0;
  }

  .shell__main {
    grid-area: main;
    padding: 32px 40px;
    overflow-x: auto;
    max-width: 1200px;
  }

  @media (max-width: 720px) {
    .shell {
      grid-template-columns: 1fr;
      grid-template-areas:
        "banner"
        "header"
        "main";
    }
    .shell__main {
      padding: 16px 14px max(20px, env(safe-area-inset-bottom));
      max-width: none;
    }
    /* Slide-over: aside is removed from layout flow and positioned
       fixed off-canvas.  Opening adds .shell--mobile-nav-open, which
       transitions it in.  width is capped to leave a peek of the
       page behind so the operator's spatial memory of "tap outside
       to dismiss" is visually obvious. */
    .shell__aside {
      position: fixed;
      top: 0;
      left: 0;
      bottom: 0;
      width: min(82vw, 320px);
      height: 100vh;
      z-index: 70;
      box-shadow: 6px 0 24px -8px rgba(15, 23, 42, 0.25);
      transform: translateX(-100%);
      transition: transform 240ms ease, visibility 0s linear 240ms;
      visibility: hidden;
      /* Aside still needs its own scroll on mobile.  Reset the
         sticky-on-desktop positioning that defaulted it to 56px
         from top. */
      padding-top: max(8px, env(safe-area-inset-top));
    }
    .shell--mobile-nav-open .shell__aside {
      transform: translateX(0);
      visibility: visible;
      transition: transform 240ms ease, visibility 0s linear 0s;
    }
    .shell__backdrop {
      position: fixed;
      inset: 0;
      z-index: 65;
      background: rgba(15, 23, 42, 0.45);
      border: 0;
      padding: 0;
      margin: 0;
      cursor: pointer;
      animation: shellBackdropIn 200ms ease;
    }
  }

  /* Reduced-motion users get the slide-over without the transition. */
  @media (prefers-reduced-motion: reduce) {
    .shell__aside { transition: none !important; }
    .shell__backdrop { animation: none; }
  }

  @keyframes shellBackdropIn {
    from { background: rgba(15, 23, 42, 0); }
    to   { background: rgba(15, 23, 42, 0.45); }
  }
`;
